import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faRepeat, faCreditCard, faBuilding, faUser } from "@fortawesome/free-solid-svg-icons";

function PaymentCard({ payment, onTogglePaid, onClick, isArchived = false }) {
  const { t } = useTranslation();

  const isInstallment = payment.type === "installment";
  const percentage = isInstallment ? (payment.paidMonths / payment.totalMonths) * 100 : 0;
  const remainingAmount = isInstallment ? payment.totalAmount - (payment.paidMonths * payment.amount) : 0;

  const handleCheckClick = (e) => {
    e.stopPropagation();
    onTogglePaid(payment.id, payment.paid);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(payment)}
      className={`relative cursor-pointer rounded-2xl p-4 transition-all duration-200 border ${
        isArchived
          ? "bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60"
          : payment.paid
          ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
          : "bg-white dark:bg-dark-surface border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-primary-200 dark:hover:border-primary-800"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Paid Checkbox */}
        {!isArchived && (
          <button
            onClick={handleCheckClick}
            className={`flex-shrink-0 w-6 h-6 mt-1 rounded-full border-2 flex items-center justify-center transition-colors ${
              payment.paid
                ? "bg-green-500 border-green-500 text-white"
                : "border-gray-300 dark:border-gray-600 text-transparent hover:border-primary-500"
            }`}
          >
            <FontAwesomeIcon icon={faCheck} size="xs" />
          </button>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <h3
                className={`font-semibold truncate ${
                  isArchived || payment.paid
                    ? "text-gray-500 dark:text-gray-500 line-through"
                    : "text-gray-900 dark:text-white"
                }`}
              >
                {payment.name}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                {/* Type Badge */}
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    isInstallment
                      ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                      : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                  }`}
                >
                  <FontAwesomeIcon icon={isInstallment ? faCreditCard : faRepeat} size="xs" />
                  {isInstallment ? t("installment", "Розстрочка") : t("recurrent", "Регулярний")}
                </span>
                
                {/* Source Badge (for installments) */}
                {isInstallment && payment.installmentSource && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      payment.installmentSource === "bank"
                        ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    <FontAwesomeIcon icon={payment.installmentSource === "bank" ? faBuilding : faUser} size="xs" />
                    {payment.installmentSource === "bank" ? t("bank", "Банк") : t("personal", "Особисті")}
                  </span>
                )}
              </div>
            </div>

            {/* Amount */}
            <div className="text-right flex-shrink-0">
              <p
                className={`text-lg font-bold ${
                  isArchived || payment.paid
                    ? "text-gray-400 dark:text-gray-600"
                    : "text-primary-600 dark:text-primary-400"
                }`}
              >
                {payment.amount} {payment.currency}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("perMonth", "/ місяць")}
              </p>
            </div>
          </div>

          {/* Installment Progress */}
          {isInstallment && (
            <div className="mt-3 space-y-2">
              {/* Progress Bar */}
              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${
                    percentage >= 100
                      ? "bg-green-500"
                      : percentage >= 66
                      ? "bg-green-500"
                      : percentage >= 33
                      ? "bg-orange-500"
                      : "bg-red-500"
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(percentage, 100)}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>

              {/* Progress Info */}
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  {payment.paidMonths} / {payment.totalMonths} {t("months", "міс.")}
                </span>
                <span className="font-medium text-gray-600 dark:text-gray-300">
                  {remainingAmount > 0
                    ? `${remainingAmount.toFixed(2)} ${payment.currency} ${t("remaining", "залишилось")}`
                    : t("fullyPaid", "Повністю сплачено")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default PaymentCard;

